import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Income } from "../models/Income";
import { Comment } from "../models/Comment";
import { IncomeService } from "../services/IncomeService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface IncomeContextProps {
  loading: Loadings;
  error: Errors;
  income: Income | null;
  incomes: Income[];
  comments: Comment[];
  createIncome: (
    incomeData: Omit<
      Income,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId"
    >,
  ) => Promise<string>;
  updateIncome: (
    incomeId: string,
    incomeData: Partial<Omit<Income, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteIncomeById: (incomeId: string) => Promise<void>;
  setSelectedIncome: (incomeId: string) => void;
}

const IncomeContext = createContext<IncomeContextProps | undefined>(undefined);

export const useIncome = (): IncomeContextProps => {
  const context = useContext(IncomeContext);
  if (!context) {
    throw new Error("useIncome must be used within a IncomeProvider");
  }
  return context;
};

export const IncomeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [incomes, setIncomes] = useState<Income[]>([]);
  const [income, setIncome] = useState<Income | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const fetchIncomes = async () => {
    if (!user || !currentMatterId) return;
    try {
      const docs = await MatterService.getIncomes(currentMatterId);
      setIncomes(docs);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const fetchComments = async (incomeId: string) => {
    try {
      const commentList = await IncomeService.getComments(incomeId);
      setComments(commentList);
    } catch (err) {
      console.error("Failed to fetch income comments:", err);
    }
  };

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchIncomes();
  }, [user, currentMatterId]);

  useSocketEventByIds(
    "financialitems:changed",
    ["*"],
    (updatedDocument: Income) => {
      fetchIncomes();
    },
  );

  const setSelectedIncome = async (incomeId: string) => {
    setLoading((prev) => ({ ...prev, details: true }));
    setIncome(null);
    setComments([]);
    setError((prev) => ({ ...prev, details: null }));
    const fetchedIncome = await IncomeService.getIncome(incomeId);
    setIncome(fetchedIncome);
    fetchComments(fetchedIncome._id);
    setLoading((prev) => ({ ...prev, details: false }));
  };

  const createIncome = async (
    incomeData: Omit<
      Income,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "matterId"
    >,
  ): Promise<string> => {
    if (!currentMatterId) return "";
    const newId = await IncomeService.createIncome({
      ...incomeData,
      matterId: currentMatterId,
    });
    fetchIncomes();
    return newId;
  };

  const updateIncome = async (
    incomeId: string,
    updatedData: Partial<Omit<Income, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await IncomeService.updateIncome(incomeId, {
      ...updatedData,
      matterId: currentMatterId!,
    });
  };

  const deleteIncomeById = async (incomeId: string) => {
    if (!currentMatterId) return;
    await IncomeService.deleteIncome(currentMatterId, incomeId);
    fetchIncomes();
  };

  return (
    <IncomeContext.Provider
      value={{
        loading,
        error,
        income,
        incomes,
        comments,
        createIncome,
        updateIncome,
        deleteIncomeById,
        setSelectedIncome,
      }}
    >
      {children}
    </IncomeContext.Provider>
  );
};
